/**
 * Pure aggregation and period-comparison logic for the sales report.
 *
 * Dates are plain 'YYYY-MM-DD' strings everywhere, and all arithmetic goes
 * through Date.UTC. A posting date is a calendar day, not an instant — building
 * a local Date from it shifts the day across a timezone boundary (Malta is
 * UTC+1/+2, so `new Date('2026-07-18')` renders as 17 Jul in a naive format
 * call). Keeping the strings opaque avoids the whole class of bug.
 */

export type Granularity = 'day' | 'week' | 'month' | 'quarter' | 'year';
export type ComparisonMode = 'none' | 'prev' | 'yoy';

export interface SalesRow {
  entry_no: number;
  posting_date: string;
  document_no: string;
  document_type: string | null;
  item_no: string;
  description: string | null;
  units: number;
  revenue: number;
  cogs: number;
  location_code: string;
  /** BC Store dimension: the store credited with the sale, not where the stock left from. */
  store_code: string | null;
  serial_no: string | null;
  created_by: string | null;
  channel: string;
  is_return: boolean;
  family: string | null;
  platform: string | null;
}

export interface DateRange {
  from: string;
  to: string;
}

// ── Date primitives (UTC-only, string in / string out) ───────────────────────

const DAY_MS = 86_400_000;

export function isValidISODate(iso: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const t = Date.parse(`${iso}T00:00:00Z`);
  return !Number.isNaN(t) && toISO(t) === iso;
}

function toISO(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

function ms(iso: string): number {
  return Date.parse(`${iso}T00:00:00Z`);
}

export function addDays(iso: string, n: number): string {
  return toISO(ms(iso) + n * DAY_MS);
}

export function daysInclusive(from: string, to: string): number {
  return Math.round((ms(to) - ms(from)) / DAY_MS) + 1;
}

/** Shift by whole months, clamping the day to the target month's length. */
export function addMonths(iso: string, n: number): string {
  const d = new Date(ms(iso));
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const target = new Date(Date.UTC(y, m + n, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  return toISO(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(day, lastDay)));
}

export function todayISO(): string {
  // The local calendar day, then treated as an opaque string from here on.
  const now = new Date();
  return toISO(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

/** Monday-based week start, matching ISO 8601. */
export function startOfISOWeek(iso: string): string {
  const dow = new Date(ms(iso)).getUTCDay(); // 0 = Sunday
  const back = dow === 0 ? 6 : dow - 1;
  return addDays(iso, -back);
}

export function isoWeekNumber(iso: string): number {
  const thursday = addDays(startOfISOWeek(iso), 3);
  const year = new Date(ms(thursday)).getUTCFullYear();
  const firstThursday = addDays(startOfISOWeek(`${year}-01-04`), 3);
  return Math.round((ms(thursday) - ms(firstThursday)) / (7 * DAY_MS)) + 1;
}

/** First day of the bucket `iso` falls in. Buckets are the x-axis positions. */
export function startOfBucket(iso: string, g: Granularity): string {
  const d = new Date(ms(iso));
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  switch (g) {
    case 'day': return iso;
    case 'week': return startOfISOWeek(iso);
    case 'month': return toISO(Date.UTC(y, m, 1));
    case 'quarter': return toISO(Date.UTC(y, Math.floor(m / 3) * 3, 1));
    case 'year': return toISO(Date.UTC(y, 0, 1));
  }
}

export function nextBucket(bucketStart: string, g: Granularity): string {
  switch (g) {
    case 'day': return addDays(bucketStart, 1);
    case 'week': return addDays(bucketStart, 7);
    case 'month': return addMonths(bucketStart, 1);
    case 'quarter': return addMonths(bucketStart, 3);
    case 'year': return addMonths(bucketStart, 12);
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Short axis label. Kept terse — the tooltip carries the full range. */
export function bucketLabel(bucketStart: string, g: Granularity): string {
  const d = new Date(ms(bucketStart));
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  switch (g) {
    case 'day': return `${day} ${MONTHS[m]}`;
    case 'week': return `${day} ${MONTHS[m]}`;
    case 'month': return `${MONTHS[m]} ${String(y).slice(2)}`;
    case 'quarter': return `Q${Math.floor(m / 3) + 1} ${String(y).slice(2)}`;
    case 'year': return String(y);
  }
}

/** Unambiguous label for tooltips and the table view. */
export function bucketLongLabel(bucketStart: string, g: Granularity): string {
  const d = new Date(ms(bucketStart));
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  switch (g) {
    case 'day': return `${day} ${MONTHS[m]} ${y}`;
    case 'week': return `Week ${isoWeekNumber(bucketStart)} · ${day} ${MONTHS[m]} ${y}`;
    case 'month': return `${MONTHS[m]} ${y}`;
    case 'quarter': return `Q${Math.floor(m / 3) + 1} ${y}`;
    case 'year': return String(y);
  }
}

export function formatISODate(iso: string | null | undefined): string {
  if (!iso || !isValidISODate(iso.slice(0, 10))) return '—';
  const d = new Date(ms(iso.slice(0, 10)));
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

// ── Preset ranges ────────────────────────────────────────────────────────────

export type RangePreset =
  | 'last_7' | 'last_30' | 'last_90' | 'mtd' | 'qtd' | 'ytd' | 'last_12m' | 'all' | 'custom';

export const RANGE_PRESETS: { value: RangePreset; label: string }[] = [
  { value: 'last_7', label: 'Last 7 days' },
  { value: 'last_30', label: 'Last 30 days' },
  { value: 'last_90', label: 'Last 90 days' },
  { value: 'mtd', label: 'Month to date' },
  { value: 'qtd', label: 'Quarter to date' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'last_12m', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom range…' },
];

/**
 * `earliest` bounds the 'all' preset — pass the oldest posting date on record so
 * the axis doesn't stretch back to 1970.
 */
export function presetRange(preset: RangePreset, earliest: string | null): DateRange {
  const today = todayISO();
  switch (preset) {
    case 'last_7': return { from: addDays(today, -6), to: today };
    case 'last_30': return { from: addDays(today, -29), to: today };
    case 'last_90': return { from: addDays(today, -89), to: today };
    case 'mtd': return { from: startOfBucket(today, 'month'), to: today };
    case 'qtd': return { from: startOfBucket(today, 'quarter'), to: today };
    case 'ytd': return { from: startOfBucket(today, 'year'), to: today };
    case 'last_12m': return { from: addDays(addMonths(today, -12), 1), to: today };
    case 'all':
    case 'custom':
    default:
      return { from: earliest ?? startOfBucket(today, 'year'), to: today };
  }
}

/** Sensible bucket size for a range — avoids 365 day-columns on a year view. */
export function suggestGranularity(range: DateRange): Granularity {
  const days = daysInclusive(range.from, range.to);
  if (days <= 31) return 'day';
  if (days <= 120) return 'week';
  if (days <= 800) return 'month';
  return 'quarter';
}

/**
 * The range the current one is measured against.
 *  - 'prev' — the equally long stretch immediately before (WOW / MOM depending
 *    on how long the selected range is)
 *  - 'yoy'  — the same calendar dates a year earlier
 */
export function comparisonRange(range: DateRange, mode: ComparisonMode): DateRange | null {
  if (mode === 'none') return null;
  if (mode === 'yoy') {
    return { from: addMonths(range.from, -12), to: addMonths(range.to, -12) };
  }
  const len = daysInclusive(range.from, range.to);
  const to = addDays(range.from, -1);
  return { from: addDays(to, -(len - 1)), to };
}

export function comparisonLabel(range: DateRange, mode: ComparisonMode): string {
  if (mode === 'none') return '';
  if (mode === 'yoy') return 'vs same period last year';
  const days = daysInclusive(range.from, range.to);
  if (days === 7) return 'vs previous week';
  if (days >= 28 && days <= 31) return 'vs previous month';
  return `vs previous ${days} days`;
}

// ── Aggregation ──────────────────────────────────────────────────────────────

/**
 * Revival packs are sold as a SKU but are not machines we built, so they never
 * belong in a PC unit count. They get their own metric instead of being folded
 * in or silently dropped: the money is real and has to keep reconciling against
 * Business Central, but "units sold" has to mean PCs.
 */
export const REVIVAL_PACK_PREFIX = 'AOI-REV';

export function isRevivalPack(itemNo: string): boolean {
  return itemNo.startsWith(REVIVAL_PACK_PREFIX);
}

export interface SalesTotals {
  /** PCs only. Revival packs are counted in packUnits, never here. */
  units: number;
  /** Net revival-pack units. Its own metric — never added into `units`. */
  packUnits: number;
  /** All products, packs included — this has to tie out to Business Central. */
  revenue: number;
  cogs: number;
  margin: number;
  /** null when revenue is 0 — a percentage of nothing is not 0%, it's undefined. */
  marginPct: number | null;
  /**
   * Average selling price of a PC: PC revenue over PC units. Pack money is
   * excluded from both halves, otherwise packs would drag the average of a
   * figure that is supposed to describe machines. null when PC units is 0.
   */
  asp: number | null;
  /** PC returns only — it annotates the PC unit tile. */
  returnUnits: number;
  entries: number;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export function aggregate(rows: SalesRow[]): SalesTotals {
  let units = 0, packUnits = 0, revenue = 0, cogs = 0, returnUnits = 0;
  let pcRevenue = 0;   // ASP denominator's other half; packs excluded from both
  for (const r of rows) {
    if (isRevivalPack(r.item_no)) {
      packUnits += r.units;
    } else {
      units += r.units;
      pcRevenue += Number(r.revenue);
      if (r.units < 0) returnUnits += -r.units;
    }
    revenue += Number(r.revenue);
    cogs += Number(r.cogs);
  }
  revenue = round2(revenue);
  cogs = round2(cogs);
  const margin = round2(revenue - cogs);
  return {
    units,
    packUnits,
    revenue,
    cogs,
    margin,
    marginPct: revenue !== 0 ? (margin / revenue) * 100 : null,
    asp: units !== 0 ? round2(pcRevenue / units) : null,
    returnUnits,
    entries: rows.length,
  };
}

/** Percentage change. null when there's no baseline to compare against. */
export function pctDelta(current: number, prior: number): number | null {
  if (prior === 0) return null;
  return ((current - prior) / Math.abs(prior)) * 100;
}

export interface SeriesPoint extends SalesTotals {
  /** Bucket start, 'YYYY-MM-DD' — the stable identity of the x position. */
  key: string;
  label: string;
  longLabel: string;
  /** Same bucket in the comparison range, aligned by index. */
  priorRevenue: number | null;
  priorUnits: number | null;
  priorMargin: number | null;
}

/** Every bucket between from and to, zero-filled so gaps read as zero not absent. */
export function enumerateBuckets(range: DateRange, g: Granularity): string[] {
  const out: string[] = [];
  let cur = startOfBucket(range.from, g);
  const end = ms(range.to);
  // Guard against a pathological range producing an unbounded axis.
  while (ms(cur) <= end && out.length < 2000) {
    out.push(cur);
    cur = nextBucket(cur, g);
  }
  return out;
}

export function bucketTotals(rows: SalesRow[], range: DateRange, g: Granularity): Map<string, SalesTotals> {
  const groups = new Map<string, SalesRow[]>();
  for (const r of rows) {
    const k = startOfBucket(r.posting_date, g);
    const arr = groups.get(k);
    if (arr) arr.push(r); else groups.set(k, [r]);
  }
  const out = new Map<string, SalesTotals>();
  for (const k of enumerateBuckets(range, g)) {
    out.set(k, aggregate(groups.get(k) ?? []));
  }
  return out;
}

/**
 * The trend series. The comparison period is aligned **by index**, not by date:
 * bucket 1 of the current range sits against bucket 1 of the prior range, which
 * is what makes a 30-day-vs-previous-30-day chart line up. Dates differ between
 * the two, so the tooltip names both.
 */
export function buildSeries(
  rows: SalesRow[],
  range: DateRange,
  g: Granularity,
  priorRows?: SalesRow[],
  priorRange?: DateRange | null,
): SeriesPoint[] {
  const current = bucketTotals(rows, range, g);
  const prior = priorRows && priorRange ? bucketTotals(priorRows, priorRange, g) : null;
  const priorKeys = prior ? [...prior.keys()] : [];

  return [...current.entries()].map(([key, totals], i) => {
    const p = prior ? prior.get(priorKeys[i]) : undefined;
    return {
      ...totals,
      key,
      label: bucketLabel(key, g),
      longLabel: bucketLongLabel(key, g),
      priorRevenue: p ? p.revenue : null,
      priorUnits: p ? p.units : null,
      priorMargin: p ? p.margin : null,
    };
  });
}

export interface Breakdown extends SalesTotals {
  key: string;
  label: string;
}

/** Key of the folded tail row. Not a real category — never filter on it. */
export const OTHER_KEY = '__other__';

/** Group by any dimension, sorted by a chosen measure, with an "Other" tail. */
export function breakdown(
  rows: SalesRow[],
  keyFn: (r: SalesRow) => string,
  labelFn: (key: string, rows: SalesRow[]) => string,
  sortBy: 'revenue' | 'units' | 'margin' = 'revenue',
  limit?: number,
): Breakdown[] {
  const groups = new Map<string, SalesRow[]>();
  for (const r of rows) {
    const k = keyFn(r) || '—';
    const arr = groups.get(k);
    if (arr) arr.push(r); else groups.set(k, [r]);
  }

  const all: Breakdown[] = [...groups.entries()]
    .map(([key, rs]) => ({ ...aggregate(rs), key, label: labelFn(key, rs) }))
    .sort((a, b) => b[sortBy] - a[sortBy]);

  if (!limit || all.length <= limit) return all;

  // Fold the tail rather than growing the palette past its ceiling.
  const head = all.slice(0, limit);
  const tail = all.slice(limit);
  const tailRows = tail.flatMap(t => groups.get(t.key) ?? []);
  head.push({ ...aggregate(tailRows), key: OTHER_KEY, label: `Other (${tail.length})` });
  return head;
}

/**
 * Display name for a SKU. Descriptions drift for the same item_no as BC items
 * get renamed, so pick the one from the most recent entry and take the part
 * before the spec string ("AOI ONE G4 - R5 | 16GB | …" → "AOI ONE G4").
 */
export function modelLabel(itemNo: string, rows: SalesRow[]): string {
  let latest: SalesRow | null = null;
  for (const r of rows) {
    if (!r.description) continue;
    if (!latest || r.posting_date > latest.posting_date) latest = r;
  }
  const desc = latest?.description;
  if (!desc) return itemNo;
  const name = desc.split(' - ')[0].trim();
  return name || itemNo;
}

export const FAMILY_NAMES: Record<string, string> = {
  '1': 'ONE',
  Z: 'ZERO',
  E: 'ELITE',
  P: 'PRO',
  M: 'MASTER',
  L: 'LEGEND',
  C: 'CUSTOM',
};

export const PLATFORM_NAMES: Record<string, string> = {
  A: 'AMD',
  I: 'Intel',
};

export function familyLabel(code: string | null): string {
  if (!code) return 'Other';
  return FAMILY_NAMES[code] ?? code;
}

export function platformLabel(code: string | null): string {
  if (!code) return 'Other';
  return PLATFORM_NAMES[code] ?? code;
}

/** One row of public.sales_stores. */
export interface SalesStore {
  code: string;
  store: string;
  store_group: string;
  closed: boolean;
}

export interface ResolvedStore {
  /** Display name, and the grouping key: retired codes share their store's name. */
  name: string;
  group: string | null;
  closed: boolean;
  /** False when the code is not in sales_stores — shown raw so a new BC store surfaces at once. */
  known: boolean;
}

export const NO_STORE = 'No store code';

export function resolveStore(code: string | null, stores: ReadonlyMap<string, SalesStore>): ResolvedStore {
  const trimmed = code?.trim() ?? '';
  if (!trimmed) return { name: NO_STORE, group: null, closed: false, known: false };
  const s = stores.get(trimmed);
  if (!s) return { name: trimmed, group: null, closed: false, known: false };
  return { name: s.store, group: s.store_group, closed: s.closed, known: true };
}

/** POS terminals post as <SHOP>_POS<n>; everything else is a named staff login. */
export function isPosUser(createdBy: string | null): boolean {
  return !!createdBy && /_POS\d*$/.test(createdBy);
}

/** Tidy a BC username for display without pretending to know the person's name. */
export function repLabel(createdBy: string | null): string {
  if (!createdBy) return '—';
  // Orphaned BC service accounts show up as USER_<32 hex chars>.
  if (/^USER_[0-9A-F]{32}$/i.test(createdBy)) return 'Unknown BC user';
  if (isPosUser(createdBy)) return createdBy.replace(/_/g, ' ');
  return createdBy;
}
